import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StackableModal } from '../components/nextgen';
import DataTable from '../components/DataTable';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import {
	addModuleFields,
	getModule,
	getModuleFieldsBeta,
	updateModuleFieldActive,
} from '../api/modules';
import { getDocuments } from '../api/documents';
import usePrevious from '../hooks/usePrevious';
import Button from '../components/Button';
import { FIELD_OPTIONS } from '../components/helpers/constants';
import { capitalizeFirstLetter } from '../components/helpers/functions';
import {
	Attribute,
	TemplateBoolean,
	TemplateField,
	TemplateReactSelect,
} from '../components/Attributes/Attributes_old';
import { classNames } from '../components/helpers/functions';
import useRequired from '../hooks/useRequired';
import BetaAttributes from '../components/Attributes';
import {
	PencilIcon,
	PencilSquareIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';

export default () => {
	const navigate = useNavigate();
	const [isCreateAttributeModal, setCreateAttributeModal] = useState(false);
	const { open: modalData, setClose } = useModal('manageAttributes');
	const { setOpen: openCustomFieldModal } = useModal('customField');
	const [form, setForm] = useState({});
	const [inputForm, setInputForm] = useState({});
	const [inputModal, setInputModal] = useState(false);

	const { value: attributes, execute: onRefreshAttributes } = useAsync(
		getModuleFieldsBeta,
		{
			defaultPayload: {
				id: modalData?.moduleId,
			},
			immediate: false,
		}
	);

	const { execute: onAddModuleField, status: addingField } = useAsync(
		addModuleFields,
		{
			immediate: false,
			showNotification: { error: 'raw' },
		}
	);

	const { value: moduleDetails, execute: onRefreshModule } = useAsync(
		getModule,
		{ immediate: false }
	);

	const { execute: onSetFieldActive } = useAsync(updateModuleFieldActive, {
		immediate: false,
	});

	const prevCreateAttributeModal = usePrevious(isCreateAttributeModal);
	useEffect(() => {
		if (!isCreateAttributeModal && prevCreateAttributeModal) {
			setForm({});
		}
	}, [isCreateAttributeModal]);

	const prevModalData = usePrevious(modalData);
	useEffect(() => {
		if (modalData && !prevModalData) {
			onRefreshAttributes({ id: modalData?.moduleId });
			onRefreshModule({ id: modalData?.moduleId });
			if (modalData?.createNew) {
				setCreateAttributeModal(true);
			}

			if (modalData?.attribute?.id) {
				setInputModal(modalData?.attribute?.attribute_field);
				setInputForm({
					[modalData?.attribute?.attribute_field?.key]: {
						field: modalData?.attribute?.attribute_field?.id,
						value: modalData?.attribute?.value,
					},
				});
			}
		}
	}, [modalData]);

	const hasAllRequired = useRequired(form, ['name', 'type']);

	return (
		<>
			<StackableModal
				title="Create a new field"
				open={!!isCreateAttributeModal}
				onClose={() => setCreateAttributeModal(false)}
				size="lg"
			>
				<div className="grid gap-y-4">
					<TemplateField
						name="Field Name"
						placeholder="Name, address, etc"
						onChange={(index, name) => setForm({ ...form, name })}
						value={form?.name}
					/>
					<TemplateReactSelect
						name="Type"
						placeholder="Select an field type"
						options={FIELD_OPTIONS}
						onChange={(index, type) => setForm({ ...form, type })}
						value={form?.type}
					/>
					{/*<TemplateBoolean
						span={1}
						name="Required"
						className="mr-3 mt-2"
						onChange={(index, required) => setForm({ ...form, required })}
						value={form?.required}
					/>*/}
					<TemplateBoolean
						span={1}
						className="mt-2"
						name="Searchable"
						onChange={(index, searchable) => setForm({ ...form, searchable })}
						value={form?.searchable}
					/>

					<div
						className={classNames(
							'col-span-2 flex mt-3',
							form?.id ? 'justify-between' : 'justify-end'
						)}
					>
						{form?.id && (
							<Button
								type="danger"
								onClick={() =>
									setDeleteModal({
										title: 'Are you sure you want to delete this field?',
										description:
											'Deleting this field will delete all data created using this field.  Please make sure this is what you want to do',
										confirmText: 'Absolutely Delete',
										onConfirm: () =>
											onDeleteModuleField(
												{
													id: module?.id,
													fieldId: form?.id,
												},
												() => {
													closeDeleteModal();
													setCreateFieldOpen(false);
													onRefresh({ id: module?.id }, null, {
														silent: true,
													});
												}
											),
									})
								}
								className="sm:ml-0"
							>
								Delete
							</Button>
						)}
						<Button
							disabled={!hasAllRequired}
							onClick={() => {
								form?.id
									? onEditModuleField(
											{
												id: module?.id,
												fieldId: form?.id,
												name: form?.name,
												type: form?.type?.value,
												searchable: form?.searchable,
												required: form?.required,
											},
											() => {
												setCreateFieldOpen(false);
												onRefresh({ id: module?.id }, null, { silent: true });
											}
									  )
									: onAddModuleField(
											{
												id: modalData?.moduleId,
												name: form?.name,
												type: form?.type?.value,
												searchable: form?.searchable,
												required: form?.required,
												active: true,
											},
											() => {
												onRefreshAttributes({ id: modalData?.moduleId }, null, {
													silent: true,
												});
												setCreateAttributeModal(false);
												setForm({});
											}
									  );
							}}
						>
							{form?.id
								? editingField === 'pending'
									? 'Saving...'
									: 'Save'
								: addingField === 'pending'
								? 'Creating...'
								: 'Create'}
						</Button>
					</div>
				</div>
			</StackableModal>
			<StackableModal
				open={!!modalData && !modalData?.attribute?.id}
				onClose={() => setClose(false)}
				//name="manageAttributes"
				size="lg"
				title={
					<div className="flex items-center justify-between">
						<div>{`Fields for: ${moduleDetails?.name}`}</div>
						<div
							className="flex items-center text-gray-500 text-xs cursor-pointer"
							onClick={() => setCreateAttributeModal(true)}
						>
							Create New
							<PlusIcon className="w-5 h-5 text-gray-600 cursor-pointer font-medium ml-1.5" />{' '}
						</div>
					</div>
				}
				showCloseIcon={false}
			>
				<DataTable
					disableTitle
					useSearch={false}
					useExport={false}
					usePagination={false}
					data={{
						rows: attributes?.combined
							?.filter((o) => !o.isDefault)
							.sort((a, b) => new Date(a?.createdAt) - new Date(b?.createdAt)),
					}}
					containerClassName={'-mx-10'}
					titleClassName={'!mb-0'}
					className={'!mb-0'}
					rowClassName={'group-hover/row:cursor-pointer'}
					columns={[
						{
							header: 'Name',
							accessorKey: 'label',
							cell: (info) => {
								return (
									<div className="flex items-center">
										{info.getValue() || null}
									</div>
								);
							},
						},
						{
							header: 'Type',
							accessorKey: 'type',
							cell: (info) => {
								return FIELD_OPTIONS.find((o) => o.value === info.getValue())
									?.label || info.getValue()
									? capitalizeFirstLetter(info.getValue())
									: null;
							},
						},
						...(typeof modalData?.onSelect === 'function'
							? [
									{
										header: '',
										accessorKey: 'row',
										cell: (info) => {
											return (
												<>
													<TemplateBoolean
														value={info?.row?.original?.isActive}
														onChange={(index, active) => {
															/*modalData?.onSelect(
															info?.row?.original?.id,
															active
														);*/

															onSetFieldActive(
																{
																	id: modalData?.moduleId,
																	fieldId: info?.row?.original?.id,
																	active,
																},
																() => {
																	onRefreshAttributes(
																		{ id: modalData?.moduleId },
																		null,
																		{ silent: true }
																	);
																}
															);
														}}
													/>
												</>
											);
											return (
												<Button
													type="ghost"
													className="text-xxs"
													onClick={() => {
														modalData?.onSelect(info?.row?.original);
													}}
												>
													Select
												</Button>
											);
										},
										enableSorting: false,
									},
							  ]
							: []),
						{
							header: '',
							accessorKey: 'row',
							id: 'edit-row',
							cell: (info) => {
								console.log('info: ', info?.row?.original);
								return (
									<div
										className="flex items-center invisible group-hover/row:visible cursor-pointer "
										onClick={() =>
											openCustomFieldModal({
												moduleId: modalData?.moduleId,
												fieldId: info?.row?.original?.id,
												field: info?.row?.original,
												onRefresh: onRefreshAttributes,
											})
										}
									>
										Edit
										<PencilSquareIcon className="ml-1.5 w-3 h-3 text-gray-500" />
									</div>
								);
							},
						},
						...(typeof modalData?.onInput === 'function'
							? [
									{
										header: '',
										accessorKey: 'row',
										cell: (info) => {
											return modalData?.skipFields?.includes(
												info?.row?.original?.id
											) ? (
												<div className="text-xxs text-gray-400 py-2 font-semibold">
													ALREADY ADDED
												</div>
											) : (
												<Button
													type="ghost"
													className="text-xxs"
													onClick={() => {
														setInputModal(info?.row?.original);
													}}
												>
													Add Field
												</Button>
											);
										},
										enableSorting: false,
									},
							  ]
							: []),
					]}
					manualSorting={false}
				/>

				<div className="flex items-center justify-end mt-4">
					<Button onClick={() => setClose(false)}>Done</Button>
				</div>
			</StackableModal>
			<StackableModal
				size="lg"
				open={!!inputModal}
				onClose={() => {
					setInputForm({});
					setInputModal(false);
					//setClose();
				}}
			>
				{inputModal && (
					<BetaAttributes
						field={inputModal}
						onChange={(index, value, fullValue) => {
							setInputForm({
								...inputForm,
								[index]: { field: inputModal?.id, value },
							});
						}}
						form={inputForm}
						//updatedForm={inputForm}
						moduleId={modalData?.moduleId}
					/>
				)}
				<div
					className={classNames(
						'flex  mt-4',
						modalData?.attribute?.id ? 'justify-between' : 'justify-end'
					)}
				>
					{modalData?.attribute?.id ? (
						<Button
							type="danger"
							onClick={() => {
								modalData?.onDelete();
								setInputForm({});
								setInputModal(null);
								setClose(false);
							}}
						>
							Delete
						</Button>
					) : null}
					<Button
						disabled={!inputForm[inputModal?.key]?.value}
						onClick={() => {
							modalData?.onInput(inputForm, inputModal);
							setInputForm({});
							setInputModal(null);
							setClose(false);
						}}
					>
						Save
					</Button>
				</div>
			</StackableModal>
		</>
	);
};
